import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthContext } from '../App';

const CentroEvaluaciones = () => {
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  
  // Helper para verificar si es estudiante o pastor
  const isStudentRole = (rol) => ['estudiante', 'pastor', 'graduado'].includes(rol);
  
  const [clases, setClases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstadoClase, setFiltroEstadoClase] = useState('activas'); // Por defecto mostrar solo activas

  useEffect(() => {
    if (user) {
      cargarClases();
    }
  }, [user]);

  const cargarClases = async () => {
    if (!user?.id) {
      console.error('❌ No hay usuario o ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      console.log('🔍 Cargando clases para usuario:', user.id, 'rol:', user.rol);
      
      const response = await axios.get(`${API}/clases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('📚 Total clases recibidas:', response.data.length);

      let clasesFiltradas = [];

      if (user.rol === 'admin') {
        // Admin ve TODAS las clases
        clasesFiltradas = response.data;
        console.log('✅ Admin - Todas las clases:', clasesFiltradas.length);
      } else if (user.rol === 'profesor') {
        // Profesor ve: clases que dicta + clases donde es estudiante
        clasesFiltradas = response.data.filter(clase => 
          clase.profesor_id === user.id || 
          (clase.estudiantes_inscritos && clase.estudiantes_inscritos.includes(user.id))
        );
        console.log('✅ Profesor - Clases (dicta + inscrito):', clasesFiltradas.length);
      } else if (isStudentRole(user.rol)) {
        // Estudiante ve solo sus clases inscritas
        clasesFiltradas = response.data.filter(clase => 
          clase.estudiantes_inscritos && clase.estudiantes_inscritos.includes(user.id)
        );
        console.log('✅ Estudiante - Clases inscritas:', clasesFiltradas.length);
      }

      setClases(clasesFiltradas);
    } catch (error) {
      console.error('Error cargando clases:', error);
      toast.error('Error al cargar las clases');
    } finally {
      setLoading(false);
    }
  };

  const handleCrearEvaluacion = () => {
    if (!selectedClaseForNew) {
      toast.error('Por favor selecciona una clase');
      return;
    }
    // Redirigir a la vista de crear formulario con la clase pre-seleccionada
    navigate(`/app/clases/${selectedClaseForNew}?action=crear-formulario`);
  };

  const handleVerPruebasClase = (claseId) => {
    navigate(`/app/pruebas-examenes?clase_id=${claseId}`);
  };

  const handleVerClaseCompleta = (claseId) => {
    navigate(`/app/clases/${claseId}`);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
            <p className="text-gray-600">Cargando evaluaciones...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <button 
            onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard'}
            className="text-gray-600 hover:text-gray-800 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="back-button"
          >
            <i className="fas fa-arrow-left"></i>
            Volver
          </button>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2 mb-2">
          <span className="text-3xl">📝</span>
          Evaluaciones
        </h1>
        <p className="text-gray-600">
          Acceso rápido a pruebas y exámenes de {isStudentRole(user.rol) ? 'tus' : user.rol === 'admin' ? 'todas las' : 'tus'} clases
        </p>
      </div>

      {/* Filtros por estado de clase */}
      <div className="mb-6 flex flex-wrap gap-2">
        <span className="text-sm font-medium text-gray-700 flex items-center">
          <i className="fas fa-filter mr-2"></i>
          Filtrar por clases:
        </span>
        <button
          onClick={() => setFiltroEstadoClase('todas')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filtroEstadoClase === 'todas'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Todas
        </button>
        <button
          onClick={() => setFiltroEstadoClase('activas')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filtroEstadoClase === 'activas'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Activas
        </button>
        <button
          onClick={() => setFiltroEstadoClase('inactivas')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filtroEstadoClase === 'inactivas'
              ? 'bg-gray-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Inactivas
        </button>
      </div>

      {/* Mis Clases */}
      <div>

        {clases.filter(clase => {
          // Filtrar por estado de clase
          if (filtroEstadoClase === 'activas') return clase.activa === true;
          if (filtroEstadoClase === 'inactivas') return clase.activa === false;
          return true; // 'todas'
        }).length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <i className="fas fa-inbox text-5xl text-gray-400 mb-4"></i>
            <p className="text-gray-600 text-lg mb-2">No tienes clases asignadas</p>
            <p className="text-gray-500 text-sm">
              Contacta con el administrador para que te asigne clases
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {clases.filter(clase => {
              // Filtrar por estado de clase
              if (filtroEstadoClase === 'activas') return clase.activa === true;
              if (filtroEstadoClase === 'inactivas') return clase.activa === false;
              return true; // 'todas'
            }).map(clase => (
              <div
                key={clase.id}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200"
              >
                {/* Banner */}
                <div className="relative h-40 overflow-hidden">
                  {clase.banner_url ? (
                    <img
                      src={clase.banner_url}
                      alt={clase.nombre}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                      <i className="fas fa-book text-5xl text-white opacity-50"></i>
                    </div>
                  )}
                </div>

                {/* Datos de la clase */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 text-lg mb-3 line-clamp-2">
                    {clase.titulo || clase.nombre}
                  </h3>

                  {/* Información de la clase */}
                  <div className="space-y-2 mb-4 text-sm text-gray-600">
                    {/* Días de clase */}
                    {clase.dias_semana && clase.dias_semana.length > 0 && (
                      <div className="flex items-center gap-2">
                        <i className="fas fa-calendar-alt text-blue-600 w-4"></i>
                        <span className="capitalize">{clase.dias_semana.join(', ')}</span>
                      </div>
                    )}
                    
                    {/* Número de estudiantes */}
                    {(clase.estudiantes_inscritos || clase.estudiantes) && (
                      <div className="flex items-center gap-2">
                        <i className="fas fa-users text-purple-600 w-4"></i>
                        <span>
                          {(clase.estudiantes_inscritos?.length || clase.estudiantes?.length || 0)} estudiante{(clase.estudiantes_inscritos?.length || clase.estudiantes?.length || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}

                    {/* Profesor */}
                    {clase.profesor_nombre && (
                      <div className="flex items-center gap-2">
                        <i className="fas fa-chalkboard-teacher text-green-600 w-4"></i>
                        <span className="truncate">{clase.profesor_nombre}</span>
                      </div>
                    )}
                  </div>

                  {/* Botón Pruebas y Exámenes */}
                  <button
                    onClick={() => handleVerPruebasClase(clase.id)}
                    className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-clipboard-check"></i>
                    Pruebas y Exámenes
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CentroEvaluaciones;
